import React, { useMemo, useState } from "react";

/* ============================================================================
   DriverCompletePage — the sign-off gate for the Route Sections project.

   Purpose (Paul's words): confirm all data is loaded and every issue is
   addressed, one driver at a time, so no driver slips through and nothing
   reaches Australia Post without attention on it.

   It does NOT redo the section analysis — it consumes a roster summary the
   engine already produces and tracks review/sign-off on top. Build the roster
   from the engine's exports:

     const roster = darlDrivers.map((driver) => {
       const dataLoaded = events.some((e) => e.driver === driver);
       const plan = buildPlan(events, driver, registry[driver]);
       const nom  = nominateSections(plan.streets);
       const flags = detectFlags(nom, registry[driver], groupSections(nom), driver);
       return { driver, dataLoaded, parcels: nom.reduce((a,s)=>a+s.parcels,0), flags };
     });
     <DriverCompletePage roster={roster} onOpenDriver={(d) => goToReview(d)} />

   Completion is session state (parked alongside override persistence — a saved,
   timestamped sign-off is the team decision).
   ========================================================================== */

const DEMO = [
  { driver: "CHENZ19", dataLoaded: true, parcels: 152, flags: [
      { sev: "review", ref: "Orwell Road", msg: "Not on route list — looks like “Orwell Street”." },
      { sev: "watch", ref: "Section 2", msg: "Carried by one street (Victoria Street, 37%)." } ] },
  { driver: "GALLEGUIL", dataLoaded: true, parcels: 220, flags: [
      { sev: "watch", ref: "South Dowling Street", msg: "Wide span (4h 20m) — likely two passes." } ] },
  { driver: "NGUYENHAT", dataLoaded: true, parcels: 168, flags: [] },
  { driver: "ZHENGZ3", dataLoaded: true, parcels: 159, flags: [
      { sev: "watch", ref: "Balance", msg: "Heaviest section 2.1× the lightest." } ] },
  { driver: "MEDINAHEM2", dataLoaded: true, parcels: 199, flags: [
      { sev: "review", ref: "Mcleay Street", msg: "Not on route list — looks like “Macleay Street”." },
      { sev: "review", ref: "Little Oxford", msg: "Not on route list — check for a wrong-driver scan." } ] },
  { driver: "YUM5", dataLoaded: true, parcels: 181, flags: [
      { sev: "review", ref: "Maclway Street", msg: "Not on route list — looks like “Macleay Street”." },
      { sev: "review", ref: "Elizabethbay Road", msg: "Not on route list — looks like “Elizabeth Bay Road”." },
      { sev: "watch", ref: "Section 2", msg: "Carried by one street (Macleay Street, 57%)." } ] },
  { driver: "XIEL5", dataLoaded: true, parcels: 159, flags: [
      { sev: "review", ref: "Darlinghurst Rd", msg: "Out-of-range number on a shared street." } ] },
  { driver: "KIMJ8", dataLoaded: false, parcels: 0, flags: [] },
];

const reviewCount = (d) => d.flags.filter((f) => f.sev === "review").length;
const watchCount = (d) => d.flags.filter((f) => f.sev === "watch").length;

function statusOf(d, reviewed) {
  if (!d.dataLoaded) return "missing";
  if (reviewed) return "complete";
  if (reviewCount(d) > 0) return "issues";
  return "ready";
}
const STATUS = {
  missing: { label: "No data", color: "#C2410C", soft: "#FEF1E7" },
  issues: { label: "Open issues", color: "#B8761B", soft: "#FBF1DC" },
  ready: { label: "Ready to confirm", color: "#5667C6", soft: "#E9ECFA" },
  complete: { label: "Complete", color: "#1B9C84", soft: "#E3F4F0" },
};

export default function DriverCompletePage({ roster = DEMO, area = "DARL", contract = "N22000", onOpenDriver }) {
  const [done, setDone] = useState({}); // driver -> true
  const [open, setOpen] = useState(null); // expanded driver

  const rows = useMemo(
    () => roster.map((d) => ({ ...d, reviewed: !!done[d.driver], status: statusOf(d, !!done[d.driver]) })),
    [roster, done]
  );

  const total = rows.length;
  const complete = rows.filter((r) => r.status === "complete").length;
  const missing = rows.filter((r) => r.status === "missing").length;
  const issues = rows.filter((r) => r.status === "issues").length;
  const pctDone = total ? Math.round((complete / total) * 100) : 0;

  const gate =
    missing > 0
      ? { tone: "stop", t: `Not ready — ${missing} driver${missing > 1 ? "s have" : " has"} no data loaded`, s: "Load the missing day(s) before anything goes to AP." }
      : complete < total
      ? { tone: "wait", t: `In progress — ${complete} of ${total} confirmed`, s: `${total - complete} driver${total - complete > 1 ? "s" : ""} still need${total - complete > 1 ? "" : "s"} your review.` }
      : { tone: "go", t: `All ${total} drivers reviewed and confirmed`, s: "Every driver has been checked — safe to share with AP." };

  const toggle = (driver) => setDone((m) => ({ ...m, [driver]: !m[driver] }));

  return (
    <div className="rcp">
      <style>{CSS}</style>

      <div className="rcp-top">
        <div>
          <div className="rcp-eyebrow">Route Sections · {area} · {contract}</div>
          <div className="rcp-title">Driver Complete</div>
        </div>
        <div className="rcp-prog">
          <div className="rcp-prog-n mono">{complete}<span>/{total}</span></div>
          <div className="rcp-prog-l">confirmed</div>
        </div>
      </div>

      <div className={"rcp-gate " + gate.tone}>
        <span className="rcp-gate-ic">{gate.tone === "go" ? "✓" : gate.tone === "stop" ? "✕" : "•"}</span>
        <div>
          <div className="rcp-gate-t">{gate.t}</div>
          <div className="rcp-gate-s">{gate.s}</div>
        </div>
        <div className="rcp-bar"><div className="rcp-bar-fill" style={{ width: pctDone + "%" }} /></div>
      </div>

      <div className="rcp-list">
        {rows.map((d) => {
          const st = STATUS[d.status];
          const rc = reviewCount(d), wc = watchCount(d);
          const isOpen = open === d.driver;
          return (
            <div key={d.driver} className={"rcp-row " + d.status + (isOpen ? " open" : "")}>
              <div className="rcp-row-main" onClick={() => setOpen(isOpen ? null : d.driver)}>
                <span className="rcp-check" style={{ borderColor: st.color, background: d.reviewed ? st.color : "transparent" }}>
                  {d.reviewed ? "✓" : ""}
                </span>
                <span className="rcp-driver mono">{d.driver}</span>
                <span className="rcp-pill" style={{ color: st.color, background: st.soft }}>{st.label}</span>
                <span className="rcp-counts">
                  {d.dataLoaded ? <span className="rcp-pcl mono">{d.parcels}/day</span> : <span className="rcp-pcl dim">no scans</span>}
                  {rc > 0 && <span className="rcp-c review">{rc} to address</span>}
                  {wc > 0 && <span className="rcp-c watch">{wc} to note</span>}
                </span>
                <span className="rcp-caret">{isOpen ? "▾" : "▸"}</span>
              </div>

              {isOpen && (
                <div className="rcp-detail">
                  {!d.dataLoaded && (
                    <div className="rcp-msg stop">No delivery scans found for {d.driver} in the loaded data. Check the audit export covers this driver before confirming.</div>
                  )}
                  {d.dataLoaded && d.flags.length === 0 && (
                    <div className="rcp-msg ok">No issues flagged — section plan is clean.</div>
                  )}
                  {d.flags.map((f, i) => (
                    <div key={i} className="rcp-flag">
                      <span className={"rcp-sev " + f.sev}>{f.sev}</span>
                      <span className="rcp-fref mono">{f.ref}</span>
                      <span className="rcp-fmsg">{f.msg}</span>
                    </div>
                  ))}
                  <div className="rcp-actions">
                    {onOpenDriver && d.dataLoaded && (
                      <button className="rcp-btn ghost" onClick={() => onOpenDriver(d.driver)}>Open full review →</button>
                    )}
                    {d.dataLoaded && !d.reviewed && (
                      <button className="rcp-btn go" onClick={() => toggle(d.driver)}>
                        {rc > 0 ? `Confirm ${rc} issue${rc > 1 ? "s" : ""} addressed & complete` : "Mark reviewed & complete"}
                      </button>
                    )}
                    {d.reviewed && (
                      <button className="rcp-btn undo" onClick={() => toggle(d.driver)}>Reopen</button>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="rcp-foot">
        Only confirmed drivers should be included in anything sent to AP. A driver counts as complete once its data is loaded and you've signed it off here.
        <br /><span className="dim">Sign-offs are session-only for now — a saved, timestamped record is parked with the team for the persistence decision.</span>
      </div>
    </div>
  );
}

const CSS = `
.rcp{--ink:#16202B;--muted:#5C6875;--line:#E4E8ED;--bg:#F6F8FA;
  font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif;color:var(--ink);
  background:var(--bg);padding:22px;border-radius:14px;max-width:880px;}
.rcp *{box-sizing:border-box}
.rcp .mono{font-family:ui-monospace,"SF Mono",Menlo,Consolas,monospace;font-variant-numeric:tabular-nums;}
.rcp-top{display:flex;justify-content:space-between;align-items:flex-end;}
.rcp-eyebrow{font-size:11px;letter-spacing:.18em;text-transform:uppercase;color:var(--muted);font-weight:600;}
.rcp-title{font-size:23px;font-weight:700;margin:2px 0 0;letter-spacing:-.01em;}
.rcp-prog{text-align:right;}
.rcp-prog-n{font-size:26px;font-weight:800;line-height:1;}.rcp-prog-n span{font-size:15px;color:#9AA4AF;font-weight:600;}
.rcp-prog-l{font-size:10px;text-transform:uppercase;letter-spacing:.1em;color:var(--muted);font-weight:600;margin-top:2px;}

.rcp-gate{display:grid;grid-template-columns:auto 1fr;gap:0 13px;align-items:center;
  border-radius:12px;padding:15px 18px;margin:18px 0 20px;border:1px solid;}
.rcp-gate.go{background:#F0FAF7;border-color:#BFE6DD;}
.rcp-gate.wait{background:#FBF7EE;border-color:#ECD9A8;}
.rcp-gate.stop{background:#FEF3EC;border-color:#F4CDB0;}
.rcp-gate-ic{grid-row:span 2;width:34px;height:34px;border-radius:50%;display:flex;align-items:center;justify-content:center;
  font-size:16px;font-weight:800;color:#fff;}
.rcp-gate.go .rcp-gate-ic{background:#1B9C84;}.rcp-gate.wait .rcp-gate-ic{background:#C99A3A;}.rcp-gate.stop .rcp-gate-ic{background:#C2410C;}
.rcp-gate-t{font-size:14px;font-weight:700;}
.rcp-gate-s{font-size:12px;color:var(--muted);margin-top:1px;}
.rcp-bar{grid-column:2;height:5px;border-radius:4px;background:rgba(0,0,0,.07);margin-top:10px;overflow:hidden;}
.rcp-bar-fill{height:100%;background:#1B9C84;border-radius:4px;transition:width .25s ease;}

.rcp-list{border:1px solid var(--line);border-radius:12px;overflow:hidden;background:#fff;}
.rcp-row{border-top:1px solid var(--line);}.rcp-row:first-child{border-top:none;}
.rcp-row.open{background:#FBFCFD;}
.rcp-row-main{display:flex;align-items:center;gap:11px;padding:12px 15px;cursor:pointer;}
.rcp-row-main:hover{background:#F4F7F9;}
.rcp-check{width:19px;height:19px;border-radius:6px;border:1.5px solid;flex-shrink:0;color:#fff;font-size:12px;font-weight:800;display:flex;align-items:center;justify-content:center;}
.rcp-driver{font-size:14px;font-weight:700;min-width:104px;}
.rcp-pill{font-size:10.5px;font-weight:700;padding:3px 9px;border-radius:20px;white-space:nowrap;}
.rcp-counts{margin-left:auto;display:flex;align-items:center;gap:8px;}
.rcp-pcl{font-size:11px;color:var(--muted);}.rcp-pcl.dim{color:#B6BEC7;}
.rcp-c{font-size:10px;font-weight:700;padding:2px 7px;border-radius:20px;}
.rcp-c.review{color:#B8761B;background:#FBF1DC;}.rcp-c.watch{color:#6B7785;background:#EEF1F4;}
.rcp-caret{color:#9AA4AF;font-size:11px;width:12px;text-align:center;}

.rcp-detail{padding:4px 15px 14px 45px;}
.rcp-msg{font-size:12px;padding:9px 12px;border-radius:8px;margin:6px 0;}
.rcp-msg.stop{color:#9A3412;background:#FEF3EC;}
.rcp-msg.ok{color:#0C5A4C;background:#EFF8F5;}
.rcp-flag{display:flex;align-items:baseline;gap:9px;padding:7px 0;border-top:1px solid #EEF1F4;}
.rcp-flag:first-of-type{border-top:none;}
.rcp-sev{flex-shrink:0;font-size:8.5px;font-weight:800;text-transform:uppercase;letter-spacing:.07em;padding:2px 6px;border-radius:20px;color:#fff;}
.rcp-sev.review{background:#B8761B;}.rcp-sev.watch{background:#6B7785;}
.rcp-fref{flex-shrink:0;font-size:11.5px;font-weight:700;min-width:120px;}
.rcp-fmsg{font-size:11.5px;color:var(--muted);line-height:1.4;}
.rcp-actions{display:flex;gap:8px;margin-top:12px;flex-wrap:wrap;}
.rcp-btn{font-size:12px;font-weight:700;padding:8px 14px;border-radius:8px;cursor:pointer;border:1px solid transparent;font-family:inherit;}
.rcp-btn.go{background:#1B9C84;color:#fff;}.rcp-btn.go:hover{background:#178573;}
.rcp-btn.ghost{background:#fff;border-color:var(--line);color:var(--ink);}.rcp-btn.ghost:hover{background:#F4F7F9;}
.rcp-btn.undo{background:#fff;border-color:#ECD9A8;color:#8A610F;}.rcp-btn.undo:hover{background:#FBF1DC;}

.rcp-foot{margin-top:18px;font-size:12px;color:var(--muted);line-height:1.55;}.rcp-foot .dim{color:#9AA4AF;}
@media (max-width:680px){
  .rcp-counts{width:100%;margin:6px 0 0 30px;}
  .rcp-row-main{flex-wrap:wrap;}
  .rcp-fref{min-width:0;flex-basis:100%;}
}
@media (prefers-reduced-motion:reduce){.rcp-bar-fill{transition:none;}}
`;
